import { LOGIN_TYPES } from '../Redux/Login/types';
import StorageManager from './StorageManager';

// Helper to safely parse JSON from StorageManager (now handled automatically)
export const safelyParseJSON = (key, defaultValue = null) => {
  return StorageManager.get(key, defaultValue);
};

// Single source of truth for checking admin status
// Phase 1C: Updated for sidecar model — checks both legacy and new patterns
export const isUserAdmin = (userOrAuthState) => {
  if (!userOrAuthState) return false;
  
  // Phase 1C: New canonical check — user has elevated to admin mode
  if (userOrAuthState.isInAdminMode) return true;
  
  // Legacy checks (original behavior preserved)
  if (userOrAuthState.isAdmin) return true;
  return userOrAuthState?.role === 'admin' || userOrAuthState?.accountType === 'admin';
};

// Phase 1C: Can this user elevate to admin mode? (has linked Admin doc)
// True even when NOT currently in admin mode. Used to show/hide rabbit icon.
export const canElevateToAdmin = (authState) => {
  if (!authState) return false;
  if (authState.user?.hasAdminAccess) return true;
  if (authState.hasAdminAccess) return true;
  // Legacy fallback
  return isUserAdmin(authState) || isUserAdmin(authState?.user);
};

// Phase 1C: Get the user's real membership tier. No more admin→VIP force-mapping.
// After sidecar, admin's User doc has a real tier (e.g., 'vip').
export const getUserTier = (authState) => {
  if (!authState) return 'base';
  return authState.user?.membershipTier || authState.profile?.membershipTier || 'base';
};

// Phase 1C: Get admin level if in admin mode, null otherwise
export const getAdminLevel = (authState) => {
  if (!authState) return null;
  if (authState.isInAdminMode) return authState.adminLevel || 'admin';
  if (isUserAdmin(authState) || isUserAdmin(authState?.user)) {
    return authState.adminLevel || authState.user?.adminLevel || 'admin';
  }
  return null;
};

// Centralized function to sync auth state from StorageManager to Redux
export const syncAuthStateFromStorage = (dispatch) => {
  try {
    const token = StorageManager.get('burrowtoken');
    const userData = StorageManager.get('burrowUserData');
    const isAdmin = StorageManager.get('isAdmin', false);

    if (token && userData) {
      console.log('Syncing auth state from storage:', { 
        userDataExists: Boolean(userData),
        token: token ? `${token.substring(0, 10)}...` : null,
        isAdmin,
        userData: userData // Log full userData for debugging
      });

      dispatch({
        type: LOGIN_TYPES.SET_USER_DATA,
        payload: {
          user: userData,
          isAdmin
        }
      });
      
      dispatch({
        type: LOGIN_TYPES.LOGIN,
        payload: {
          email: userData.burrowerId,
          token,
          user: userData,
          isAdmin
        }
      });
    } else {
      console.log('No complete auth data in StorageManager to sync');
    }
  } catch (error) {
    console.error('Error syncing auth state from storage:', error);
  }
};

// Decode JWT payload without a library (base64url → JSON)
const decodeJwtPayload = (token) => {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
};

// Verify auth state: check token exists AND is not expired
export const verifyAuthState = (authState, dispatch, logoutAction) => {
  // If we have auth state but no token in StorageManager, log out
  if (authState?.isAuth && !StorageManager.get('burrowtoken')) {
    console.warn('Auth state mismatch: Redux shows logged in but StorageManager has no token');
    dispatch(logoutAction());
    return false;
  }

  // If token exists, check if it's expired
  const token = StorageManager.get('burrowtoken');
  if (token) {
    const payload = decodeJwtPayload(token);
    if (payload?.exp && (payload.exp * 1000) < Date.now()) {
      console.warn('Auth token expired — logging out. Expired at:', new Date(payload.exp * 1000).toISOString());
      StorageManager.remove('burrowtoken');
      StorageManager.remove('burrowUserData');
      StorageManager.remove('isAdmin');
      StorageManager.remove('adminLevel');
      dispatch(logoutAction());
      return false;
    }
  }
  return true;
};

// Add debug helper function to diagnose auth issues
export const debugAuthState = () => {
  const token = StorageManager.get('burrowtoken');
  const userData = StorageManager.get('burrowUserData');
  const isAdmin = StorageManager.get('isAdmin', false);
  
  console.log('Auth State Debug:', { 
    hasToken: Boolean(token), 
    hasUserData: Boolean(userData),
    isAdmin,
    userData
  });
  
  return {
    hasToken: Boolean(token),
    hasUserData: Boolean(userData),
    isAdmin,
    userData
  };
};
